import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Save, FlaskConical, Trophy, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const SLOTS = [
  { key: "hero", label: "Hero background", hint: "Full-width top of the homepage" },
  { key: "gauge", label: "GAUGE section", hint: "'A compass for conscious participation'" },
  { key: "wellness", label: "Offerings section", hint: "Also used on the Offerings page hero" },
  { key: "community", label: "Community section", hint: "Full-width background band" },
  { key: "stewardship", label: "Stewardship section", hint: "Also used on the Stewardship hero" },
  { key: "events", label: "Events / Spirals", hint: "Fallback when no events are scheduled" },
  { key: "membership", label: "Affiliate Locations card", hint: "Also used on Locations & Membership heroes" },
];

const TEXT_FIELDS = [
  { key: "hero_label", label: "Hero eyebrow label", type: "input" },
  { key: "hero_title", label: "Hero headline", type: "textarea" },
  { key: "hero_subtitle", label: "Hero subheadline", type: "textarea" },
];

export default function HomepageContentPanel() {
  const { data, isLoading } = useQuery({ queryKey: ["homepage-content"], queryFn: () => api.get("/site/homepage").then((r) => r.data) });
  const { data: ab, refetch: refetchAb } = useQuery({ queryKey: ["ab-hero"], queryFn: () => api.get("/admin/ab/hero").then((r) => r.data), refetchInterval: 15000 });
  const [images, setImages] = useState({});
  const [text, setText] = useState({});
  const [heroCtas, setHeroCtas] = useState([]);
  const [cards, setCards] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) { setImages(data.images || {}); setText(data.text || {}); setHeroCtas(data.hero_ctas || []); setCards(data.cards || []); }
  }, [data]);

  const setListItem = (setter) => (idx, field, val) => setter((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const setCta = setListItem(setHeroCtas);
  const setCard = setListItem(setCards);

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/admin/site/homepage", { images, text, hero_ctas: heroCtas, cards });
      setImages(res.images); setText(res.text); setHeroCtas(res.hero_ctas); setCards(res.cards);
      toast.success("Homepage updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[#144F75]" /></div>;

  const resetAb = async () => {
    if (!window.confirm("Reset the A/B test counters to zero?")) return;
    try { await api.delete("/admin/ab/hero"); await refetchAb(); toast.success("A/B counters reset"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const Bar = ({ label, v, winner }) => {
    const conv = v?.conversion ?? 0;
    return (
      <div className={`rounded-xl border p-4 ${winner ? "border-[#5B8A72] bg-[#5B8A72]/[0.06]" : "border-[#E5E0D8]"}`} data-testid={`ab-stat-${label.toLowerCase()}`}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold text-[#144F75] uppercase tracking-wide">Variant {label}{winner && <span className="ml-2 inline-flex items-center gap-1 text-[#5B8A72]"><Trophy size={12} /> Leading</span>}</p>
          <span className="font-serif text-2xl text-[#1A242B]">{conv}%</span>
        </div>
        <p className="text-sm text-[#4A5D6B] italic mb-2 line-clamp-2">"{v?.title || (label === "A" ? "(hero headline)" : "(not set)")}"</p>
        <p className="text-xs text-[#8FA89B]">{v?.views || 0} views · {v?.gauge_clicks || 0} started GAUGE</p>
      </div>
    );
  };

  return (
    <div data-testid="homepage-content-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Homepage content</h2>
          <p className="text-sm text-[#4A5D6B]">Edit hero copy, buttons, entry cards, and imagery. Clear a field to fall back to the default.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="homepage-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={15} />} Save changes
        </button>
      </div>

      {/* Hero copy */}
      <div className="eyw-card p-6 mb-6" data-testid="homepage-copy">
        <p className="eyw-label mb-4">Hero copy</p>
        <div className="space-y-4">
          {TEXT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs text-[#4A5D6B] mb-1 block">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea rows={2} value={text[f.key] || ""} onChange={(e) => setText((t) => ({ ...t, [f.key]: e.target.value }))}
                  data-testid={`homepage-text-${f.key}`} className="eyw-input w-full resize-none" />
              ) : (
                <input value={text[f.key] || ""} onChange={(e) => setText((t) => ({ ...t, [f.key]: e.target.value }))}
                  data-testid={`homepage-text-${f.key}`} className="eyw-input w-full" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Hero A/B test */}
      <div className="eyw-card p-6 mb-6" data-testid="homepage-ab">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <p className="eyw-label flex items-center gap-1.5"><FlaskConical size={13} /> Hero A/B test</p>
          <button onClick={resetAb} data-testid="ab-reset" className="text-xs text-[#8FA89B] hover:text-[#B5794E] inline-flex items-center gap-1"><RotateCcw size={12} /> Reset counters</button>
        </div>
        <p className="text-xs text-[#8FA89B] mb-4">Set a second headline to split visitors 50/50 and see which invites more people to start GAUGE. Leave blank to turn the test off (everyone sees the main headline).</p>
        <label className="text-xs text-[#4A5D6B] mb-1 block">Variant B headline (optional)</label>
        <textarea rows={2} value={text.hero_title_b || ""} onChange={(e) => setText((t) => ({ ...t, hero_title_b: e.target.value }))}
          data-testid="homepage-text-hero_title_b" placeholder="e.g. Enhance how you relate to yourself, others, and the world." className="eyw-input w-full resize-none" />
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Bar label="A" v={ab?.variant_a} winner={ab?.winner === "a"} />
          <Bar label="B" v={ab?.variant_b} winner={ab?.winner === "b"} />
        </div>
        {!ab?.enabled && <p className="text-xs text-[#8FA89B] mt-3">Test is currently off — add a Variant B headline above and Save to start splitting traffic.</p>}
      </div>

      {/* Hero buttons */}
      <div className="eyw-card p-6 mb-6" data-testid="homepage-ctas">
        <p className="eyw-label mb-4">Hero buttons</p>
        <div className="space-y-3">
          {heroCtas.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-3" data-testid={`homepage-cta-${i}`}>
              <input value={c.label || ""} onChange={(e) => setCta(i, "label", e.target.value)} placeholder="Button label" data-testid={`homepage-cta-label-${i}`} className="eyw-input w-full" />
              <input value={c.to || ""} onChange={(e) => setCta(i, "to", e.target.value)} placeholder="Link (e.g. /wellness)" data-testid={`homepage-cta-to-${i}`} className="eyw-input w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Begin Where You Are cards */}
      <div className="eyw-card p-6 mb-8" data-testid="homepage-cards">
        <p className="eyw-label mb-1">"Begin Where You Are" cards</p>
        <p className="text-xs text-[#8FA89B] mb-4">Label + destination for each entry card (icons stay fixed).</p>
        <div className="space-y-3">
          {cards.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-3" data-testid={`homepage-card-${i}`}>
              <input value={c.label || ""} onChange={(e) => setCard(i, "label", e.target.value)} placeholder="Card label" data-testid={`homepage-card-label-${i}`} className="eyw-input w-full" />
              <input value={c.to || ""} onChange={(e) => setCard(i, "to", e.target.value)} placeholder="Link (e.g. /gauge/app)" data-testid={`homepage-card-to-${i}`} className="eyw-input w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Imagery */}
      <p className="eyw-label mb-4">Imagery</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SLOTS.map((s) => (
          <div key={s.key} className="eyw-card p-5" data-testid={`homepage-slot-${s.key}`}>
            <ImageUpload value={images[s.key] || ""} onChange={(url) => setImages((im) => ({ ...im, [s.key]: url }))} label={s.label} testid={`homepage-img-${s.key}`} />
            {s.hint && <p className="text-xs text-[#8FA89B] mt-2">{s.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
